<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  defineExpose,
} from "vue";

const props = defineProps<{
  initial?: "collapsed" | "expanded";
  collapsedRatio?: number;
}>();

const emit = defineEmits<{
  (e: "heightChange", value: number): void;
}>();

const COLLAPSED = computed(() => props.collapsedRatio ?? 0.18);
const EXPANDED = 0.75;

const isUserInteracting = ref(false);

const vh = () => window.innerHeight;

const height = ref(
  props.initial === "expanded" ? vh() * EXPANDED : vh() * COLLAPSED.value,
);

watch(
  height,
  (newHeight) => {
    emit("heightChange", newHeight);
  },
  { immediate: true },
);

watch(
  () => props.collapsedRatio,
  (newRatio) => {
    if (!newRatio) return;
    height.value = vh() * newRatio;
  },
);

const isDragging = ref(false);
const startY = ref(0);
const startHeight = ref(0);

const isExpanded = computed(() => height.value >= vh() * (EXPANDED - 0.05));

const onPointerDown = (e: PointerEvent) => {
  e.preventDefault();

  isUserInteracting.value = true;
  isDragging.value = true;
  startY.value = e.clientY;
  startHeight.value = height.value;

  document.addEventListener("pointermove", onPointerMove, { passive: false });
  document.addEventListener("pointerup", onPointerUp);
  document.addEventListener("pointercancel", onPointerUp);
};

const onPointerMove = (e: PointerEvent) => {
  if (!isDragging.value) return;

  e.preventDefault();

  const delta = startY.value - e.clientY;
  const next = startHeight.value + delta;

  height.value = Math.min(
    vh() * EXPANDED,
    Math.max(vh() * COLLAPSED.value, next),
  );
};

const onPointerUp = () => {
  isDragging.value = false;

  const midPoint = (vh() * COLLAPSED.value + vh() * EXPANDED) / 2;

  if (height.value < midPoint) {
    height.value = vh() * COLLAPSED.value;
  } else {
    height.value = vh() * EXPANDED;
  }

  // Reset user interaction after animation
  setTimeout(() => {
    isUserInteracting.value = false;
  }, 300);

  document.removeEventListener("pointermove", onPointerMove);
  document.removeEventListener("pointerup", onPointerUp);
  document.removeEventListener("pointercancel", onPointerUp);
};

onMounted(() => {
  const handleResize = () => {
    const ratio = height.value / window.innerHeight;
    height.value = vh() * ratio;
  };

  window.addEventListener("resize", handleResize);

  onBeforeUnmount(() => {
    window.removeEventListener("resize", handleResize);
  });
});

onBeforeUnmount(() => {
  document.removeEventListener("pointermove", onPointerMove);
  document.removeEventListener("pointerup", onPointerUp);
  document.removeEventListener("pointercancel", onPointerUp);
});

const expandTo = () => {
  isUserInteracting.value = false;
  requestAnimationFrame(() => {
    const content = document.querySelector(
      ".sheet-content",
    ) as HTMLElement | null;
    if (!content) return;

    const contentHeight = content.scrollHeight + 28;

    height.value = Math.min(
      vh() * EXPANDED,
      Math.max(vh() * COLLAPSED.value, contentHeight),
    );
  });
};

const collapseTo = () => {
  isUserInteracting.value = false;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      height.value = vh() * COLLAPSED.value;
    });
  });
};

defineExpose({
  expandTo,
  collapseTo,
  isUserInteracting,
  getHeight: () => height.value,
});
</script>

<template>
  <section
    class="bottom-sheet"
    :class="{ expanded: isExpanded, dragging: isDragging }"
    :style="{ height: `${height}px` }"
  >
    <div class="sheet-handle-area" @pointerdown="onPointerDown">
      <div class="sheet-handle">
        <span />
      </div>
    </div>

    <div class="sheet-content">
      <slot />
    </div>
  </section>
</template>

<style>
.bottom-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;

  background: #fff;
  border-radius: 24px 24px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);

  max-height: min(75vh, 760px);
  overflow: hidden;

  z-index: 10;

  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  -webkit-overflow-scrolling: touch;
  touch-action: none;
  user-select: none;
}

.bottom-sheet.dragging {
  transition: none;
}

.sheet-handle-area {
  padding: 10px 24px 0;
  cursor: grab;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
}

.sheet-handle-area:active {
  cursor: grabbing;
}

.sheet-handle {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.sheet-handle span {
  display: block;
  width: 40px;
  height: 4px;
  border-radius: 999px;
  background: #d1d5db;
  transition: background 0.2s;
}

.sheet-handle-area:active .sheet-handle span {
  background: #9ca3af;
}

.sheet-content {
  height: calc(100% - 30px);
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 0 24px 24px;
  touch-action: pan-y;
}

.bottom-sheet.expanded .sheet-content {
  overflow-y: auto;
}

@supports (-webkit-touch-callout: none) {
  .bottom-sheet {
    max-height: 75vh;
  }

  .sheet-content {
    overscroll-behavior-y: contain;
  }
}

@media (max-width: 768px) {
  .bottom-sheet {
    border-radius: 20px 20px 0 0;
  }

  .sheet-handle span {
    width: 48px;
    height: 5px;
  }
}
</style>
